import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import theme from "../../../shared-ui/src/theme/index";
import darkTheme from "shared-ui/src/theme/darkTheme";
import {
  useGetUserConfig,
  useUpsertUserConfig,
} from "../modules/hooks/useUserConfig";
import { UserConfigDto } from "@lib/ShiOneClient";
import { isEmbeddedMode } from "../utils/embeddedMode";

const ThemeModeProviderContext = createContext({
  toggleThemeMode: () => {},
  mode: "light",
  currentTheme: {},
});

export const ThemeModeProvider: React.FC<PropsWithChildren<any>> = ({
  children,
}) => {
  const settingsKey = "ThemeLightDarkMode";
  const { userConfig } = useGetUserConfig(settingsKey);
  const { mutate: upsertUserConfig } = useUpsertUserConfig();

  const [mode, setMode] = useState<string>("light");
  const isEmbedded = isEmbeddedMode();

  // Load theme from persisted config
  useEffect(() => {
    const savedMode = userConfig?.settingsValue;
    if (savedMode === "light" || savedMode === "dark") {
      setMode(savedMode);
    }
  }, [userConfig?.settingsValue]);

  // Listen for theme changes from parent window when embedded
  useEffect(() => {
    if (!isEmbedded) return;

    const handleMessage = (event: MessageEvent) => {
      // Validate message structure
      if (
        event.data?.type === "THEME_CHANGE" &&
        (event.data?.theme === "light" || event.data?.theme === "dark")
      ) {
        setMode(event.data.theme);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isEmbedded]);

  const providerValues = useMemo(() => {
    const toggleThemeMode = () => {
      const newMode = mode === "light" ? "dark" : "light";
      upsertUserConfig({
        settingsKey: settingsKey,
        settingsValue: newMode,
      } as UserConfigDto);
      setMode(newMode);
    };
    const currentTheme = mode === "light" ? theme : darkTheme;

    return {
      currentTheme,
      mode,
      toggleThemeMode,
    };
  }, [mode]);
  return (
    <ThemeModeProviderContext.Provider value={providerValues}>
      {children}
    </ThemeModeProviderContext.Provider>
  );
};

export function useThemeMode() {
  return useContext(ThemeModeProviderContext);
}
